/* eslint-disable no-alert */
import React, { useState, useEffect, useCallback } from "react";
import _has from "lodash/has";
import _get from "lodash/get";
import { useSelector, useDispatch } from "react-redux";
import { EyeIcon, PencilIcon, XIcon } from "@heroicons/react/outline";
import { CheckIcon } from "@heroicons/react/solid";
import { useHistory } from "react-router-dom";
import { parseISO, format } from "date-fns";
import Table from "components/lib/Global/ReactTable";
import TableActionMenu from "components/lib/Shared/TableActionMenu";
import CommentsPopup from "components/lib/Global/CommentsPopup";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";
import { resolveFields, invalidateFields } from "state/slices/resolveField";
import { roundNumber } from "utils";
import { getComments } from "state/slices/comment";
import { deleteTemplateAsync } from "state/slices/template";
import {
  deleteShareAuthorizationAsync,
  resetAuthorizationSharesTable,
  getShareAuthorizationsAsync,
} from "state/slices/shareCapitalAuthorization";
import TableSubRow from "./TableSubRow";
import { useToast } from "hooks/useToast";

const ExistingAuthorizationsTable = (props) => {
  const { company } = useSelector((state) => state.companySlice);
  const { toast } = useToast(5000);
  const proposedShares = useSelector(
    (state) => state.shareAuthorizationSlice?.shares || []
  );
  const approvedShares = proposedShares.filter(
    (share) => share.isApproved === true
  );

  const { resolvedFields } = useSelector((state) => state.companySlice);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRows, setSelectedRow] = useState([]);
  const [allFieldsResolved, setAllFieldsResolved] = useState(false);
  const dispatch = useDispatch();
  const history = useHistory();
  const currentUser = useSelector((state) => state.userSlice);
  const isViewMode = history.location.pathname.split("/").pop() === "view";
  const isReviewerMode =
    history.location.pathname.split("/").pop() === "review";
  const [expandedRow, setExpandedRow] = useState({});
  const [initialRows, setInitialRows] = useState({});

  const closeSubRow = (row) => {
    setInitialRows({});
    setExpandedRow({});
    row.toggleRowExpanded();
  };

  const handleEdit = (row, rest) => {
    if (!row.isExpanded) {
      row.toggleAllRowsExpanded(false); // Close all rows
      setInitialRows(rest.initialRows);
      setExpandedRow(row);
      row.toggleRowExpanded();
      props.setMode({
        editing: true,
        id: row.original._id,
      });
    } else {
      setInitialRows({});
      setExpandedRow({});
      row.toggleRowExpanded();
    }
  };

  const handleDelete = async (row) => {
    try {
      const deleteAction = await dispatch(
        deleteShareAuthorizationAsync({ id: row._id })
      );
      dispatch(resetAuthorizationSharesTable());
      dispatch(getShareAuthorizationsAsync(props.companyId));
      if (deleteShareAuthorizationAsync.fulfilled.match(deleteAction)) {
        const message = "Authorization deleted successfully.";
        toast("success", message);
        if (row.rightsAttached?._id) {
          await dispatch(
            deleteTemplateAsync({ templateId: row.rightsAttached._id })
          );
        }
      } else {
        const errorPayload = deleteAction.payload;
        let errorMessage;

        if (
          errorPayload &&
          typeof errorPayload === "object" &&
          errorPayload.message
        ) {
          errorMessage = errorPayload.message;
        } else if (typeof errorPayload === "string") {
          errorMessage = errorPayload;
        } else {
          errorMessage = "Failed to delete authorization. Please try again.";
        }

        console.error("❌ Error deleting authorization:", errorPayload);
        toast("error", errorMessage);
      }
    } catch (error) {
      const errorMessage =
        error?.message || "An unexpected error occurred during deletion.";
      console.error("❌ Exception during deletion:", errorMessage);
      toast("error", errorMessage);
    }
  };

  const handleResolveBtnClicked = async (fieldName) => {
    const commentsToBeResolved = props.comments?.filter(
      (comment) => comment.fieldName === fieldName && !comment.isResolved
    );
    const commentsToBeResolvedIds =
      commentsToBeResolved?.map(({ _id }) => _id) ?? [];
    setIsLoading(true);
    setSelectedRow((selectedRows) => [...selectedRows, fieldName]);
    const promise = await dispatch(
      resolveFields({
        data: {
          formId: _get(company, "_id"),
          formName: "company",
          fieldName,
          resolvedFields: [fieldName],
          commentsToBeResolvedIds,
        },
        updateEffectKey: "updateCompanyState",
      })
    );
    if (commentsToBeResolvedIds.length > 0) {
      const query = { companyId: commentsToBeResolved[0]?.company };
      if (isReviewerMode) query.createdBy = currentUser?._id;
      await dispatch(getComments(query));
    }
    setIsLoading(false);
    setSelectedRow((selectedRows) => [
      ...selectedRows.filter(
        (rowName) => rowName !== promise.meta.arg.data.fieldName
      ),
    ]);
  };

  const handleInvalidateBtnClicked = async (fieldName) => {
    setIsLoading(true);
    setSelectedRow((selectedRows) => [...selectedRows, fieldName]);
    const promise = await dispatch(
      invalidateFields({
        data: {
          formId: _get(company, "_id"),
          formName: "company",
          fieldName,
          invalidFields: [fieldName],
        },
        updateEffectKey: "updateCompanyState",
      })
    );
    setIsLoading(false);
    setSelectedRow((selectedRows) => [
      ...selectedRows.filter(
        (rowName) => rowName !== promise.meta.arg.data.fieldName
      ),
    ]);
  };

  const fieldsName = proposedShares
    .map((share) => share.sharesCategory)
    .filter(Boolean);

  useEffect(() => {
    setAllFieldsResolved(
      fieldsName.every((element) => resolvedFields.includes(element))
    );
  }, [fieldsName, resolvedFields]);

  const handleAllFieldsStatus = async (e) => {
    const commentsToBeResolved = props.comments?.filter(
      (comment) => !comment.isResolved
    );
    const commentsToBeResolvedIds =
      commentsToBeResolved?.map(({ _id }) => _id) ?? [];
    setIsLoading(true);
    if (e.target.checked) {
      const unresolvedFields = fieldsName.filter(
        (fieldName) => !resolvedFields.includes(fieldName)
      );
      setSelectedRow((selectedRows) => [...selectedRows, ...unresolvedFields]);
      await dispatch(
        resolveFields({
          data: {
            formId: _get(company, "_id"),
            formName: "company",
            resolvedFields: unresolvedFields,
            commentsToBeResolvedIds,
          },
          updateEffectKey: "updateCompanyState",
        })
      );
      if (commentsToBeResolvedIds.length > 0) {
        const query = { companyId: commentsToBeResolved[0]?.company };
        if (isReviewerMode) query.createdBy = currentUser?._id;
        await dispatch(getComments(query));
      }
      setIsLoading(false);
      setSelectedRow([]);
    } else {
      const resolvedFieldsSlice = fieldsName.filter((fieldName) =>
        resolvedFields.includes(fieldName)
      );
      setSelectedRow((selectedRows) => [
        ...selectedRows,
        ...resolvedFieldsSlice,
      ]);
      await dispatch(
        invalidateFields({
          data: {
            formId: _get(company, "_id"),
            formName: "company",
            invalidFields: resolvedFieldsSlice,
          },
          updateEffectKey: "updateCompanyState",
        })
      );
      setSelectedRow([]);
    }
    setIsLoading(false);
  };

  const sumOfVotingRights = approvedShares.reduce(
    (acc, share) => roundNumber(acc + (Number(share?.votingRights) || 0)),
    0
  );

  const data = [...approvedShares];
  if (approvedShares.length > 0) {
    data.push({
      id: "total",
      sharesCategory: "Total",
      sharesNumber: "",
      sharesValue: "",
      nominalValue: "",
      votingRights: sumOfVotingRights,
    });
  }

  const columns = [
    {
      Header: "Share Category",
      Cell: ({ row }) => {
        if (row.original.id === "total") return <b>Total</b>;
        return row.original.sharesCategory || "N/A";
      },
    },
    { Header: "Number of Shares", accessor: "sharesNumber" },
    { Header: "Value of Shares", accessor: "sharesValue" },
    { Header: "Nominal Value", accessor: "nominalValue" },
    {
      Header: "Voting Rights",
      Cell: ({ row }) => {
        if (row.original.id === "total")
          return <b>{row.original.votingRights}</b>;
        return row.original.votingRights || 0;
      },
    },
    {
      Header: "Date Issued",
      accessor: "authorizationDate",
      Cell: ({ value }) => {
        try {
          if (!value) return "";
          const parsedDate = parseISO(value);
          return format(parsedDate, "yy-MM-dd");
        } catch {
          return "Invalid date";
        }
      },
    },
    {
      Header: "Date Expiry",
      accessor: "expirationDate",
      Cell: ({ value }) => {
        try {
          if (!value) return "";
          const parsedDate = parseISO(value);
          return format(parsedDate, "yy-MM-dd");
        } catch {
          return "Invalid date";
        }
      },
    },
  ];

  if (
    props.showActionColumn &&
    !props.showAddCommentColumn &&
    !allFieldsResolved
  ) {
    columns.push({
      Header: "Actions",
      Cell: ({ row, ...rest }) => {
        if (row.original.id === "total") return "";
        return (
          <div className="flex items-center space-x-2">
            <button
              onClick={() => props.handleView(row.original)}
              className="flex space-x-1 justify-center items-center focus:outline-none mr-5 hover:text-gray-600 py-2.5 text-sm leading-3 text-gray-500"
            >
              <EyeIcon className="w-6 h-6" />
              <span>Quick View</span>
            </button>
            {row?.isExpanded ? (
              <button onClick={() => handleEdit(row, rest)}>Close</button>
            ) : (
              <TableActionMenu
                row={row}
                handleEdit={() => handleEdit(row, rest)}
                handleDelete={() => handleDelete(row.original)}
              />
            )}
          </div>
        );
      },
    });
  }

  if (props.showAddCommentColumn && !isViewMode) {
    columns.push({
      Header: "Actions",
      Cell: ({ row }) => {
        if (row.original.id === "total") return "";
        const isRowLoading =
          isLoading && selectedRows.includes(row.original.sharesCategory);

        return (
          <div className="flex items-center space-x-2">
            {!resolvedFields.includes(row.original.sharesCategory) && (
              <CommentsPopup
                handleSaveComment={props.handleSaveComment}
                formName="shareInformation"
                fieldName={row.original.sharesCategory}
                fieldLabel={row.original.sharesCategory}
                comment={props.comments.find(
                  (comment) =>
                    comment.fieldName === row.original.sharesCategory &&
                    currentUser?._id === comment.createdBy?._id
                )}
              >
                <span>Review</span>
                <PencilIcon className="inline w-4 h-4" />
              </CommentsPopup>
            )}
            <div className={`flex items-center justify-center`}>
              {resolvedFields.includes(row.original.sharesCategory) ? (
                <button
                  className={`flex space-x-1 ${
                    isRowLoading ? "cursor-not-allowed" : "cursor-pointer"
                  }`}
                  onClick={() =>
                    isRowLoading
                      ? null
                      : handleInvalidateBtnClicked(row.original.sharesCategory)
                  }
                >
                  {isRowLoading ? (
                    <SpinnerIcon className="text-primary" />
                  ) : (
                    <>
                      <span>Accepted </span>
                      <XIcon className="inline w-4 h-4" />
                    </>
                  )}
                </button>
              ) : (
                <button
                  className={`flex space-x-1 ${
                    isRowLoading ? "cursor-not-allowed" : "cursor-pointer"
                  }`}
                  onClick={() =>
                    isRowLoading
                      ? null
                      : handleResolveBtnClicked(row.original.sharesCategory)
                  }
                >
                  {isRowLoading ? (
                    <SpinnerIcon className="text-primary" />
                  ) : (
                    <>
                      <span>Accept</span>
                      <CheckIcon className="inline w-4 h-4" />
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        );
      },
    });
  }

  // Create a function that will render our row sub components
  const renderRowSubComponent = useCallback(
    ({ row, rowProps, visibleColumns }) => (
      <TableSubRow
        row={row}
        mode={props.mode}
        rowProps={rowProps}
        visibleColumns={visibleColumns}
        initialRows={initialRows}
        takenShares={props.takenShares}
        closeSubRow={() => closeSubRow(row)}
        nominalShares={proposedShares}
        isProposed={true}
      />
    ),
    [initialRows]
  );

  useEffect(() => {
    if (expandedRow?.isExpanded) expandedRow?.toggleRowExpanded();
  }, [expandedRow]);

  if (approvedShares.length < 1) {
    return <p className="text-center font-medium">No Authorization found</p>;
  }

  return (
    <div className="bg-white w-full shadow mt-4">
      <div className="w-full">
        <Table
          columns={columns}
          data={data}
          renderRowSubComponent={renderRowSubComponent}
        />
      </div>
    </div>
  );
};

export default ExistingAuthorizationsTable;
